import React, { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { PORTFOLIO } from "../../lib/data";

const CATEGORIES = ["All", "Restaurants", "Estate Agents", "Salons", "Retail", "Gyms", "Tradesmen"];

export default function Portfolio() {
  const [active, setActive] = useState("All");
  const items = useMemo(
    () => (active === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active)),
    [active]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative py-24 md:py-32 bg-[#050505]"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-12 gap-12 items-end mb-14">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-gold"></span>
              <span className="text-[11px] uppercase tracking-[0.32em] text-white/60">
                Selected Work
              </span>
            </div>
            <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              A portfolio of
              <br />
              <em className="gold-text-gradient not-italic">quiet, profitable</em>
              <br />
              luxury.
            </h2>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="text-white/60 leading-relaxed">
              From neighbourhood restaurants to high-end estate agents — every project
              is engineered to compound revenue, year after year.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" data-testid="portfolio-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setActive(c)}
              className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-all ${
                active === c
                  ? "border-gold text-black gold-bg-gradient"
                  : "border-white/15 text-white/70 hover:border-white/40 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((p, i) => (
            <article
              key={p.id}
              data-testid={`portfolio-item-${p.id}`}
              className={`group relative overflow-hidden bg-[#0f0f0f] border border-white/8 ${
                i % 5 === 0 ? "lg:col-span-2 lg:row-span-1" : ""
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent opacity-90 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute top-5 left-5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white/90 border border-white/30 backdrop-blur-md bg-black/30">
                  {p.category}
                </div>
                <div className="absolute top-5 right-5 h-10 w-10 flex items-center justify-center border border-white/30 text-white/80 group-hover:bg-gold group-hover:text-black group-hover:border-gold transition-all">
                  <ArrowUpRight size={18} />
                </div>
              </div>
              <div className="p-6 md:p-7">
                <h3 className="font-display text-white text-2xl tracking-tight mb-2">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm leading-relaxed">{p.summary}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-14 flex justify-center">
          <a
            href="#contact"
            data-testid="portfolio-cta"
            className="inline-flex items-center gap-2 px-8 py-4 text-sm font-medium border border-white/20 text-white hover:bg-white/5 hover:border-gold/40 transition-all"
          >
            Become our next case study
            <ArrowUpRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}
